import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { Brand } from "@/components/Brand";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.resetPassword({ token, password });
      toast.success("Password reset. Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <Brand variant="auth" />

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-4">
          {!token ? (
            <p className="text-sm text-red-600" data-testid="reset-no-token">This reset link is invalid. Please request a new one.</p>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div>
                <h2 className="font-display text-lg font-bold text-slate-900">Set a new password</h2>
                <p className="text-sm text-slate-500">Choose a new password for your account.</p>
              </div>
              {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}
              <div className="space-y-1.5">
                <Label>New password</Label>
                <Input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <div className="space-y-1.5">
                <Label>Confirm password</Label>
                <Input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
              </div>
              <Button data-testid="reset-submit" type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700">
                {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Resetting...</> : "Reset password"}
              </Button>
            </form>
          )}
          <div className="text-center pt-1">
            <Link to="/login" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
